import { forwardRef, type KeyboardEvent } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';

export interface InternalNoteViewItem {
  id: string;
  content: string;
  createdAt: string;
  authorLabel?: string;
}

interface InternalNotesViewProps {
  notes: InternalNoteViewItem[];
  isLoading?: boolean;
  loadError?: string;
  onRetry?: () => void;
  canCreate?: boolean;
  permissionMessage?: string;
  content?: string;
  onContentChange?: (content: string) => void;
  onSubmit?: () => void;
  isSubmitting?: boolean;
  submitError?: string;
  isRetry?: boolean;
  maxLength?: number;
  sticky?: boolean;
  loadingLabel?: string;
}

const InternalNotesView = forwardRef<HTMLTextAreaElement, InternalNotesViewProps>(
  function InternalNotesView({
    notes,
    isLoading = false,
    loadError,
    onRetry,
    canCreate = true,
    permissionMessage,
    content = '',
    onContentChange,
    onSubmit,
    isSubmitting = false,
    submitError,
    isRetry = false,
    maxLength,
    sticky = false,
    loadingLabel = 'Loading…',
  }, textareaRef) {
    const normalizedLength = content.trim().length;
    const canSubmit = normalizedLength > 0
      && (!maxLength || normalizedLength <= maxLength)
      && !isSubmitting;

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey && canSubmit) {
        event.preventDefault();
        onSubmit?.();
      }
    };

    return (
      <section
        aria-labelledby="internal-notes-heading"
        className={`rounded-xl border border-brand-border bg-white p-4 shadow-sm ${
          sticky ? 'xl:sticky xl:top-4' : ''
        }`}
      >
        <h3
          id="internal-notes-heading"
          className="mb-1 text-base font-semibold text-brand-text-primary"
        >
          Internal Notes
        </h3>
        <p className="mb-3 text-xs text-gray-500">
          These notes are internal only and not visible to the customer.
        </p>

        {canCreate && onContentChange && onSubmit && (
          <div className="mb-4 rounded-xl border border-brand-border bg-brand-surface p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-brand-text-secondary">
                Add an internal note
              </p>
              <button
                type="button"
                onClick={onSubmit}
                disabled={!canSubmit}
                className="inline-flex h-9 min-w-9 items-center justify-center rounded-full bg-brand-accent px-2 text-brand-text-inverse transition hover:bg-brand-accent/90 disabled:opacity-40"
                title={isRetry ? 'Retry note' : 'Add note'}
                aria-label={isRetry ? 'Retry note' : 'Add note'}
              >
                <PlusIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>

            <label htmlFor="internal-note-content" className="sr-only">Internal note</label>
            <textarea
              ref={textareaRef}
              id="internal-note-content"
              aria-label="Internal note"
              value={content}
              onChange={(event) => onContentChange(event.target.value)}
              placeholder="Add a note…"
              rows={2}
              maxLength={maxLength}
              className="w-full resize-none rounded-md border border-brand-border px-3 py-2 text-sm"
              onKeyDown={handleKeyDown}
            />
            <div className="mt-1 flex items-center justify-between gap-2 text-xs text-gray-400">
              <p>Tip: Press Enter to add. Use Shift + Enter for a new line.</p>
              {maxLength && <p>{normalizedLength.toLocaleString()} / {maxLength.toLocaleString()}</p>}
            </div>
            {submitError && (
              <p role="alert" className="mt-2 text-sm text-red-700">{submitError}</p>
            )}
          </div>
        )}

        {permissionMessage && (
          <p className="rounded-lg border border-dashed border-brand-border bg-brand-surface p-3 text-sm text-brand-text-secondary">
            {permissionMessage}
          </p>
        )}

        {isLoading && <p className="text-sm text-gray-400">{loadingLabel}</p>}

        {loadError && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-800">{loadError}</p>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="mt-2 rounded border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-800"
              >
                Retry internal notes
              </button>
            )}
          </div>
        )}

        {!permissionMessage && !isLoading && !loadError && notes.length === 0 && (
          <p className="rounded-lg border border-dashed border-brand-border bg-brand-surface p-3 text-sm text-brand-text-secondary">
            No internal notes yet.
          </p>
        )}

        <div className="max-h-[28rem] space-y-2 overflow-y-auto pr-1">
          {notes.map((note) => (
            <article
              key={note.id}
              data-testid="internal-note-item"
              className="flex justify-start"
            >
              <div className="w-full rounded-2xl border border-brand-border bg-white px-3 py-2 shadow-sm">
                <p className="whitespace-pre-wrap text-sm text-brand-text-primary">
                  {note.content}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-gray-400">
                  {note.authorLabel && <span>{note.authorLabel}</span>}
                  <span>{new Date(note.createdAt).toLocaleString()}</span>
                  <span className="font-medium">Internal only</span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    );
  },
);

export default InternalNotesView;
